import {
  ApplicationConfig,
  provideZoneChangeDetection,
  Injectable,
} from '@angular/core';
import {
  GuardsCheckEnd,
  NavigationEnd,
  provideRouter,
  withInMemoryScrolling,
  withPreloading,
  withComponentInputBinding,
  PreloadingStrategy,
  Route,
} from '@angular/router';

import { routes } from './app.routes';
import {
  provideClientHydration,
  withEventReplay,
  withIncrementalHydration, // ✅ NUEVO en Angular 19
} from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { ConfigService } from './config/config.service';
import { provideIcons } from './providers/icon.provider';
import { NotificationService } from './notificaction/notification.service';
import { AuthService } from './auth/services/auth.service';
import { GlobalStore } from './store/app.store';
import { medusaInterceptor } from './auth/interceptors/medusa.interceptor';
import { authBaseInterceptor } from './auth/interceptors/auth-base.interceptor';
import { provideNgProgressRouter } from 'ngx-progressbar/router';
import { environment } from '@/config/runtime-config';
import { provideNgxStripe } from 'ngx-stripe';
import {
  HTTP_OPTION_MAILISEARCH_CTX,
  HTTP_OPTION_MAILISEARCH_CTX_CONFIG,
  HTTP_OPTION_TOKEN_CTX,
  HTTP_OPTION_TOKEN_CTX_CONFIG,
} from './tokens';

import { provideToastr } from 'ngx-toastr';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Observable, of } from 'rxjs';
import { provideServiceWorker } from '@angular/service-worker';

// ✅ Estrategia de precarga selectiva para Angular 19
// Solo precarga rutas marcadas con data.preload = true
@Injectable({ providedIn: 'root' })
export class SelectivePreloadingStrategy implements PreloadingStrategy {
  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    // Precarga solo si la ruta tiene data.preload = true
    if (route.data?.['preload']) {
      console.log('Preloading:', route.path);
      return load();
    }
    return of(null);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideAnimationsAsync(),

    // ✅ Zone.js optimizado para Angular 19
    provideZoneChangeDetection({
      eventCoalescing: true,
      runCoalescing: true, // ✅ NUEVO en Angular 19 - mejora el rendimiento
    }),

    // ✅ Hydration mejorado para Angular 19
    provideClientHydration(
      withEventReplay(),
      withIncrementalHydration() // ✅ NUEVO - hidratación incremental para mejor FCP
    ),

    provideHttpClient(
      withFetch(),
      withInterceptors([medusaInterceptor, authBaseInterceptor])
    ),

    provideIcons(),

    // ✅ CAMBIO CRÍTICO: Usar estrategia selectiva en lugar de PreloadAllModules
    provideRouter(
      routes,
      withPreloading(SelectivePreloadingStrategy), // ✅ Cambiado de PreloadAllModules
      withInMemoryScrolling({
        scrollPositionRestoration: 'disabled',
        anchorScrolling: 'disabled',
      }),
      withComponentInputBinding()
    ),

    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    {
      provide: HTTP_OPTION_TOKEN_CTX_CONFIG,
      useValue: { context: HTTP_OPTION_TOKEN_CTX },
    },
    {
      provide: HTTP_OPTION_MAILISEARCH_CTX_CONFIG,
      useValue: { context: HTTP_OPTION_MAILISEARCH_CTX },
    },

    AuthService,
    ConfigService,
    GlobalStore,
    NotificationService,

    provideNgxStripe(environment.stripePublicKey),
    provideNgProgressRouter({
      startEvents: [GuardsCheckEnd],
      completeEvents: [NavigationEnd],
      minDuration: 500,
    }),
    provideToastr(),

    // ✅ ACCIÓN #2: Service Worker para cachear assets
    // Estrategia: registerWhenStable:30000
    //   - Espera a que Angular se estabilice (se terminen renders iniciales)
    //   - Máximo espera 30 segundos (timeout)
    //   - Habilitado siempre para permitir testing/debugging
    // Impacto esperado: -250ms en repeat visits por caché local
    provideServiceWorker('ngsw-worker.js', {
      enabled: true, // ✅ Habilitado siempre (desarrollo + producción)
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
